/**
 * WHERE A BANNER TAP GOES, AND HOW IT GETS THERE.
 *
 * Two destinations behind one `ctaUrl`, and they must not be opened the same
 * way. Admins can now point a banner at a screen of this very app, and
 * `window.open(path, '_blank')` — what both banner components did for
 * everything — is wrong for that in three ways: it leaves the app, it loses
 * the router state and the session warmth that make the screen instant, and
 * inside the native WebView `_blank` frequently does nothing at all, so the
 * tap is silently dead.
 *
 * TOLD APART BY SHAPE, NOT BY A FLAG. An in-app link is a relative path by
 * construction and an external one is required to be absolute http(s), so the
 * scheme is already the discriminator. Reading it here rather than trusting a
 * `ctaType` field means banners authored before the picker existed — which
 * carry no type — are routed correctly too, and the public banner payload
 * needs no new field.
 */

/** True when `ctaUrl` addresses somewhere outside this app. */
export const isExternalCta = (ctaUrl?: string): boolean =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(ctaUrl?.trim() ?? '');

/**
 * The in-app path for a CTA, or null if it points outside.
 *
 * Normalised to a leading slash because the API stores these bare ("shop") to
 * suit its own redirect, while react-router needs "/shop" — without it
 * `navigate('shop')` resolves RELATIVE to the current route and a tap from
 * `/profile/orders` would land on `/profile/orders/shop`.
 */
export const inAppCtaPath = (ctaUrl?: string): string | null => {
  const trimmed = ctaUrl?.trim();
  if (!trimmed || isExternalCta(trimmed)) return null;
  return `/${trimmed.replace(/^\/+/, '')}`;
};

/**
 * Opens a banner's CTA the right way for what it is.
 *
 * Pass react-router's `navigate`; external links never touch it.
 */
export const openBannerCta = (
  ctaUrl: string | undefined,
  navigate: (path: string) => void
): void => {
  const trimmed = ctaUrl?.trim();
  if (!trimmed) return;

  const path = inAppCtaPath(trimmed);
  if (path) {
    navigate(path);
    return;
  }

  window.open(trimmed, '_blank', 'noopener,noreferrer');
};
